import { useCallback, useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api, formatApiError } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNum } from "@/lib/format";
import { attT } from "@/lib/attendanceI18n";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function ClickPicker({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng); } });
  return null;
}

export default function GeofenceTab({ canEdit }) {
  const { theme, lang } = useI18n();
  const [s, setS] = useState({ office_lat: 24.7136, office_lng: 46.6753, radius_m: 15, min_accuracy_m: 150 });
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/attendance/settings");
      setS({
        office_lat: data.office_lat ?? 24.7136,
        office_lng: data.office_lng ?? 46.6753,
        radius_m: data.radius_m ?? 15,
        min_accuracy_m: data.min_accuracy_m ?? 150,
      });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handlePick = (latlng) => canEdit && setS((cur) => ({ ...cur, office_lat: latlng.lat, office_lng: latlng.lng }));

  const useMyLocation = () => {
    setErr("");
    if (!navigator.geolocation) { setErr(attT(lang, "geo.gps_unsupported")); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setS((cur) => ({ ...cur, office_lat: p.coords.latitude, office_lng: p.coords.longitude })),
      (e) => setErr(attT(lang, "geo.gps_fail", { err: e.message })),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const save = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      await api.put("/admin/attendance/settings", s);
      setOk(attT(lang, "saved_ok"));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!loaded) return <div style={{ padding: 40, color: theme.textMuted }}>{attT(lang, "loading")}</div>;

  const css = makeStyles(theme);
  const center = [s.office_lat, s.office_lng];

  return (
    <div data-testid="attendance-geofence-tab">
      {err && <div style={css.err}>{err}</div>}
      {ok && <div style={css.ok}>{ok}</div>}
      <div style={css.info}>{canEdit ? attT(lang, "geo.info_can_edit") : attT(lang, "geo.info_readonly")}</div>
      <div style={css.mapWrap}>
        <MapContainer center={center} zoom={18} style={{ height: 360, width: "100%", borderRadius: 12 }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Marker position={center} />
          <Circle center={center} radius={s.radius_m} pathOptions={{ color: "#d4af37", fillOpacity: 0.15 }} />
          {canEdit && <ClickPicker onPick={handlePick} />}
        </MapContainer>
      </div>
      <div style={css.grid}>
        <Field label={attT(lang, "geo.lat")} theme={theme}><input type="number" step="any" disabled={!canEdit} value={s.office_lat} onChange={(e) => setS({ ...s, office_lat: parseFloat(e.target.value) })} style={css.input} data-testid="att-lat" /></Field>
        <Field label={attT(lang, "geo.lng")} theme={theme}><input type="number" step="any" disabled={!canEdit} value={s.office_lng} onChange={(e) => setS({ ...s, office_lng: parseFloat(e.target.value) })} style={css.input} data-testid="att-lng" /></Field>
        <Field label={attT(lang, "geo.radius")} theme={theme}><input type="number" min={5} max={20} disabled={!canEdit} value={s.radius_m} onChange={(e) => setS({ ...s, radius_m: parseInt(e.target.value || 15, 10) })} style={css.input} data-testid="att-radius" /></Field>
        <Field label={attT(lang, "geo.accuracy")} theme={theme}><input type="number" min={5} max={500} disabled={!canEdit} value={s.min_accuracy_m} onChange={(e) => setS({ ...s, min_accuracy_m: parseInt(e.target.value || 150, 10) })} style={css.input} data-testid="att-acc" /></Field>
      </div>
      {canEdit && (
        <div style={css.actions}>
          <button onClick={useMyLocation} style={css.btn} data-testid="att-use-my-loc">{attT(lang, "geo.use_my_loc")}</button>
          <button onClick={save} disabled={busy} style={css.btnPrimary} data-testid="att-save">{busy ? attT(lang, "saving") : attT(lang, "geo.save")}</button>
        </div>
      )}
      <div style={css.cur}>{attT(lang, "geo.current", { lat: fmtNum(s.office_lat.toFixed(6)), lng: fmtNum(s.office_lng.toFixed(6)), r: fmtNum(s.radius_m) })}</div>
    </div>
  );
}

function Field({ label, theme, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: theme.textMuted }}>{label}</label>
      {children}
    </div>
  );
}

const makeStyles = (theme) => ({
  info: { background: theme.surfaceCard, border: `1px solid ${theme.borderAccent}`, borderRadius: 12, padding: 12, marginBottom: 14, fontSize: 13, lineHeight: 1.7 },
  err: { background: theme.errorBg, color: theme.errorText, padding: 10, borderRadius: 8, marginBottom: 10 },
  ok: { background: "rgba(48,164,108,0.15)", color: "#30a46c", padding: 10, borderRadius: 8, marginBottom: 10 },
  mapWrap: { borderRadius: 12, overflow: "hidden", border: `1px solid ${theme.border}`, marginBottom: 14 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, marginBottom: 14 },
  input: { background: theme.surface, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 14, fontFamily: "inherit" },
  actions: { display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap", marginBottom: 12 },
  btn: { background: theme.surfaceCard, color: theme.text, border: `1px solid ${theme.border}`, padding: "10px 18px", borderRadius: 10, cursor: "pointer", fontWeight: 700 },
  btnPrimary: { background: theme.gold, color: "#000", border: "none", padding: "10px 22px", borderRadius: 10, cursor: "pointer", fontWeight: 800 },
  cur: { background: theme.surfaceAlt, border: `1px dashed ${theme.border}`, borderRadius: 8, padding: 10, textAlign: "center", fontSize: 13 },
});
